import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { CheckMarkIcon, CloseIcon } from '@svgs'
import { HeadPage } from 'src/components/elements'
import { Button, Loader } from 'src/components/ui'
import { useGetPaymentByIdQuery } from 'src/redux/api'

const PENDING = ['CREATED', 'PROCESSING']

// Monobank sends the buyer back here (redirectUrl). The page polls the
// payment until the webhook (or the lazy re-sync behind the endpoint)
// settles it, then celebrates or explains the failure.
const PaymentResult: FC = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const paymentId = searchParams.get('paymentId') || ''
  const [pollingInterval, setPollingInterval] = useState(3000)

  const {
    data: payment,
    isLoading,
    isError,
  } = useGetPaymentByIdQuery(paymentId, { skip: !paymentId, pollingInterval })

  useEffect(() => {
    // A settled payment or a failing endpoint both end the polling — a 404/500
    // used to be re-fetched (and re-toasted) every three seconds forever.
    if ((payment && !PENDING.includes(payment.status)) || isError) setPollingInterval(0)
  }, [payment, isError])

  const renderBody = () => {
    if (!paymentId || isError) {
      return (
        <>
          <div className="w-16 h-16 rounded-full bg-muted-soft flex items-center justify-center">
            <span className="text-3xl leading-none text-muted">?</span>
          </div>
          <h2 className="m-0 text-[1.375rem] font-extrabold leading-7 text-ink">
            {t('payment_result.not_found')}
          </h2>
          <Button link="/cabinet">{t('payment_result.to_cabinet')}</Button>
        </>
      )
    }
    if (isLoading || !payment) {
      return <Loader />
    }
    if (PENDING.includes(payment.status)) {
      return (
        <>
          <Loader />
          <h2 className="m-0 text-[1.375rem] font-extrabold leading-7 text-ink">
            {t('payment_result.pending_title')}
          </h2>
          <p className="m-0 text-[0.9375rem] leading-[1.375rem] text-ink-2 max-w-[24rem]">
            {t('payment_result.pending_text')}
          </p>
        </>
      )
    }
    if (payment.status === 'SUCCESS') {
      return (
        <>
          <div className="w-16 h-16 rounded-full bg-success-soft flex items-center justify-center">
            <CheckMarkIcon className="w-8 h-8 text-success" />
          </div>
          <h2 className="m-0 text-[1.375rem] font-extrabold leading-7 text-ink">
            {t('payment_result.success_title')}
          </h2>
          <p className="m-0 text-[0.9375rem] leading-[1.375rem] text-ink-2 max-w-[24rem]">
            {t('payment_result.success_text')}
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button link={`/announcement/${payment.propertyId}`}>
              {t('payment_result.view_listing')}
            </Button>
            <Button link="/cabinet" variant="outlined">
              {t('payment_result.to_cabinet')}
            </Button>
          </div>
        </>
      )
    }
    // FAILURE / EXPIRED / REVERSED
    return (
      <>
        <div className="w-16 h-16 rounded-full bg-danger-soft flex items-center justify-center">
          <span className="icon-button w-6 h-6 fill-danger">
            <CloseIcon />
          </span>
        </div>
        <h2 className="m-0 text-[1.375rem] font-extrabold leading-7 text-ink">
          {t(`payment_result.${payment.status === 'REVERSED' ? 'reversed_title' : 'failed_title'}`)}
        </h2>
        <p className="m-0 text-[0.9375rem] leading-[1.375rem] text-ink-2 max-w-[24rem]">
          {t(`payment_result.${payment.status === 'REVERSED' ? 'reversed_text' : 'failed_text'}`)}
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          <Button link={`/announcement/${payment.propertyId}`}>
            {t('payment_result.try_again')}
          </Button>
          <Button link="/cabinet" variant="outlined">
            {t('payment_result.to_cabinet')}
          </Button>
        </div>
      </>
    )
  }

  return (
    <>
      <HeadPage title={t('payment_result.title')} />
      <div className="flex flex-col items-center gap-3 text-center max-w-md mx-auto w-full p-6 md:p-10 bg-surface rounded-2xl shadow-card">
        {renderBody()}
      </div>
    </>
  )
}

export default PaymentResult
